import {
    MAX_PLANNED_VIRTUAL_APPOINTMENTS,
    type OpenVirtualAppointmentsNowResult,
    type VirtualAppointmentDto
} from '../../services/virtualAppointments';
import { OpenNowDueNotice } from './OpenNowDueNotice';
import { OpenNowFooter } from './OpenNowFooter';
import { OpenNowModeField } from './OpenNowModeField';
import {
    VirtualAppointmentDetailPrompt,
    VirtualAppointmentReadOnlyDetail
} from './VirtualAppointmentDetailPanels';
import { VirtualAppointmentEditor } from './VirtualAppointmentEditor';
import { VirtualAppointmentListTabs } from './VirtualAppointmentListTabs';
import { VirtualAppointmentMasterDetail } from './VirtualAppointmentMasterDetail';
import { VirtualAppointmentSelectorList } from './VirtualAppointmentSelectorList';
import { useOpenNowForm, type OpenNowErrorsByKey, type OpenNowTab } from './useOpenNowForm';
import type { OpenVirtualAppointmentsNow } from './useOpenVirtualAppointmentsNow';
import {
    useVirtualAppointmentImageUpload,
    useVirtualAppointmentTypeOptions
} from './useVirtualAppointmentEditorSupport';
import type { OpenNowDraft, OpenNowDraftPatch, OpenNowPatientContext } from './openNowDraft';
import { weeksStartUnavailable } from './openNowMode';
import { NO_TREATMENT_START_ON_RECORD } from './virtualAppointmentWeeksHint';

function addVerb(canOpenNow: boolean): string {
    return canOpenNow ? 'open now or plan' : 'plan';
}

function addPrompt(canOpenNow: boolean): string {
    return `Add a VA to ${addVerb(canOpenNow)}.`;
}

function emptyText(tab: OpenNowTab, canOpenNow: boolean): string {
    if (tab === 'open') return 'No VAs open.';
    if (tab === 'planned') return 'No VAs planned.';
    return `No new VAs yet. Add one below to ${addVerb(canOpenNow)}.`;
}

interface OpenNowDetailProps {
    patientId: string;
    patient: OpenNowPatientContext;
    selectedKey: string | null;
    saved: VirtualAppointmentDto[];
    drafts: OpenNowDraft[];
    errorsByKey: OpenNowErrorsByKey;
    revealErrors: boolean;
    onChange: (key: string, patch: OpenNowDraftPatch) => void;
}

// An open or planned VA shows read-only; a new draft opens in the editor, its trigger once planned.
function OpenNowDetail({
    patientId,
    patient,
    selectedKey,
    saved,
    drafts,
    ...draftProps
}: OpenNowDetailProps) {
    const typeOptions = useVirtualAppointmentTypeOptions();
    const handleImageUpload = useVirtualAppointmentImageUpload(patientId);
    const existing = saved.find((va) => va.id === selectedKey);
    if (existing) return <VirtualAppointmentReadOnlyDetail va={existing} />;
    const draft = drafts.find((d) => d.key === selectedKey);
    if (!draft) return <VirtualAppointmentDetailPrompt message={addPrompt(patient.canOpenNow)} />;
    const { onChange } = draftProps;
    return (
        <VirtualAppointmentEditor
            key={draft.key}
            draft={draft}
            errors={draftProps.errorsByKey[draft.key] ?? {}}
            typeOptions={typeOptions}
            revealErrors={draftProps.revealErrors}
            onChange={onChange}
            onImageUpload={handleImageUpload}
            leading={
                <OpenNowModeField
                    value={draft.mode}
                    canOpenNow={patient.canOpenNow}
                    onChange={(mode) => onChange(draft.key, { mode })}
                />
            }
            hideTrigger={draft.mode === 'now'}
            stackTypeAndTrigger
            treatmentStartDate={patient.treatmentStartDate}
            dmLinked={patient.dmLinked}
            aligners={patient.aligners}
            treatmentStartUnavailableHint={
                weeksStartUnavailable(patient) ? NO_TREATMENT_START_ON_RECORD : undefined
            }
        />
    );
}

interface OpenVirtualAppointmentsNowFormProps {
    patientId: string;
    allowMoveOnly: boolean;
    openVas: VirtualAppointmentDto[];
    planned: VirtualAppointmentDto[];
    patient: OpenNowPatientContext;
    submitter: OpenVirtualAppointmentsNow;
    onClose: () => void;
    onOpened: (result: OpenVirtualAppointmentsNowResult) => void;
}

export function OpenVirtualAppointmentsNowForm(props: OpenVirtualAppointmentsNowFormProps) {
    const { patientId, openVas, planned, patient, submitter, onClose } = props;
    const form = useOpenNowForm(props);
    const isNew = form.tab === 'new';

    return (
        <>
            <VirtualAppointmentMasterDetail
                list={
                    <>
                        <VirtualAppointmentListTabs
                            tabs={form.tabs}
                            activeKey={form.tab}
                            onSelect={form.setTab}
                            fill
                        />
                        <VirtualAppointmentSelectorList
                            entries={form.entries}
                            selectedKey={form.selectedKey}
                            onSelect={form.select}
                            onRemove={isNew ? form.remove : undefined}
                            add={{
                                onAdd: form.handleAdd,
                                canAdd: form.canAdd,
                                disabledHint: `Up to ${MAX_PLANNED_VIRTUAL_APPOINTMENTS} VAs`
                            }}
                            emptyText={emptyText(form.tab, patient.canOpenNow)}
                        />
                    </>
                }
                detail={
                    <OpenNowDetail
                        patientId={patientId}
                        patient={patient}
                        selectedKey={form.selectedKey}
                        saved={form.tab === 'open' ? openVas : planned}
                        drafts={isNew ? form.drafts : []}
                        errorsByKey={form.errorsByKey}
                        revealErrors={form.revealErrors}
                        onChange={form.update}
                    />
                }
            />
            {submitter.error && (
                <p role="alert" className="px-5 pt-3 text-sm text-red-500">
                    {submitter.error}
                </p>
            )}
            {form.dueNotice && <OpenNowDueNotice text={form.dueNotice} />}
            <OpenNowFooter
                onCancel={onClose}
                cancelDisabled={submitter.submitting}
                submit={form.submit}
            />
        </>
    );
}
